import { useState, useEffect } from "react";
import { useMutation } from '@tanstack/react-query';
import { LanguageResponse } from "../../../types/api/lang";
import { fetchLanguages } from "../../../services/lang";
import { Stack, ActionList, ActionMenu, Spinner, Button } from '@primer/react';
import { GlobeIcon, CheckIcon } from '@primer/octicons-react'

interface LangModalProps {
    filterOpened: boolean;
    setFilterOpened: (value: boolean) => void;
    selectedLangs: LanguageResponse[];
    setSelectedFilter: (value: LanguageResponse[]) => void;
    onDismiss: () => void;
    onApply: () => void;
}

const LangModal: React.FC<LangModalProps> = ({
    filterOpened,
    setFilterOpened,
    selectedLangs,
    setSelectedFilter,
    onApply,
    onDismiss
}) => {
    const [langs, setLangs] = useState<LanguageResponse[]>([]);
    const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

    const { mutate: fetchLanguagesRequest, isPending: isLoadingLang } = useMutation({
        mutationFn: fetchLanguages,
        onSuccess: (data) => {
            setLangs(data.list);
        },
        onError: (error: any) => {
        }
    });

    useEffect(() => {
        if (langs.length > 0) {
            const indices = selectedLangs.map(selectedLang =>
                langs.findIndex(lang => lang.id === selectedLang.id)
            ).filter(index => index !== -1);
            setSelectedIndices(indices);
        }
    }, [langs, selectedLangs]);

    const toggleSelectedIndex = (index: number) => {
        setSelectedIndices((prevSelectedIndices) => {
            if (prevSelectedIndices.includes(index)) {
                return prevSelectedIndices.filter((i) => i !== index);
            } else {
                return [...prevSelectedIndices, index];
            }
        });
    };

    const handleDismiss = () => {
        // Reset
        const initialIndices = selectedLangs.map(selectedLang =>
            langs.findIndex(lang => lang.id === selectedLang.id)
        ).filter(index => index !== -1);
        setSelectedIndices(initialIndices);
        setFilterOpened(false);
        onDismiss();
    };

    const handleApply = () => {
        // Save
        const selectedLanguages = selectedIndices.map(index => langs[index]);
        setSelectedFilter(selectedLanguages);
        setFilterOpened(false);
        onApply();
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            handleDismiss();
        } else {
            setFilterOpened(open);
        }
    };

    useEffect(() => {
        fetchLanguagesRequest({});
    }, []);

    return (
        <>
            <ActionMenu open={filterOpened} onOpenChange={handleOpenChange}>
                <ActionMenu.Anchor>
                    <button className="flex items-center gap-[4px] px-2 py-[6px] border-[1px] border-[#0969DA] rounded-md bg-white hover:bg-blue-50 h-8">
                        <GlobeIcon className="w-4 h-4 text-[#0969DA]" />
                        <span className="text-[#0969DA] font-medium text-xs">Языки</span>
                    </button>
                </ActionMenu.Anchor>
                <ActionMenu.Overlay className="!w-[232px]">
                    <ActionList
                        selectionVariant="multiple"
                        role="menu"
                        aria-label="Item"
                        className="!w-[232px]"
                    >
                        {isLoadingLang && <Spinner />}
                        {langs.map((item, index) => (
                            <ActionList.Item
                                key={index}
                                onSelect={(event) => {
                                    event.preventDefault();
                                    toggleSelectedIndex(index);
                                }}
                            >
                                <ActionList.LeadingVisual>
                                    {selectedIndices.includes(index) && <CheckIcon className="-ml-10" />}
                                </ActionList.LeadingVisual>
                                <div className="-ml-2">{item.titleRu || item.titleOrigin}</div>
                            </ActionList.Item>
                        ))}
                        <Stack direction="horizontal" gap='normal' className='block mt-4 ml-[138px]'>
                            <Button
                                onClick={handleApply}
                                size='small'
                                variant="primary">
                                Применить
                            </Button>
                        </Stack>
                    </ActionList>
                </ActionMenu.Overlay>
            </ActionMenu>
        </>
    );
};
export default LangModal